import React, { useState } from 'react';
import { supabase, supabaseAdmin } from '../config/supabase';
import { useProfile } from '../contexts/ProfileContext';

interface Profile {
  id: string;
  name: string;
  email: string;
  role: string;
}

interface ResetPasswordProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

export const ResetPassword: React.FC<ResetPasswordProps> = ({ onSuccess, onError }) => {
  const { profile: currentUser } = useProfile();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<Profile | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [users, setUsers] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Función para generar una contraseña aleatoria segura
  const generateSecurePassword = () => {
    const length = 12;
    const uppercaseChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowercaseChars = 'abcdefghijklmnopqrstuvwxyz';
    const numberChars = '0123456789';
    const specialChars = '!@#$%^&*()_+-=[]{}|;:,.<>?';
    
    // Asegurarse de que la contraseña tenga al menos un carácter de cada tipo
    let password = '';
    password += uppercaseChars.charAt(Math.floor(Math.random() * uppercaseChars.length));
    password += lowercaseChars.charAt(Math.floor(Math.random() * lowercaseChars.length));
    password += numberChars.charAt(Math.floor(Math.random() * numberChars.length));
    password += specialChars.charAt(Math.floor(Math.random() * specialChars.length));
    
    // Completar el resto de la contraseña con caracteres aleatorios
    const allChars = uppercaseChars + lowercaseChars + numberChars + specialChars;
    for (let i = password.length; i < length; i++) {
      password += allChars.charAt(Math.floor(Math.random() * allChars.length));
    }
    
    // Mezclar los caracteres para que no sigan un patrón predecible
    password = password.split('').sort(() => Math.random() - 0.5).join('');
    
    setNewPassword(password);
  };

  // Función para buscar usuarios
  const searchUsers = async (search: string) => {
    try {
      const { data: profiles, error } = await supabase
        .from('profiles')
        .select('id, name, email, role')
        .or(`name.ilike.%${search}%,email.ilike.%${search}%`)
        .limit(5);

      if (error) throw error;
      setUsers(profiles || []);
    } catch (error) {
      console.error('Error al buscar usuarios:', error);
      onError?.(error instanceof Error ? error.message : 'Error al buscar usuarios');
    }
  };

  // Validar contraseña
  const validatePassword = (password: string): boolean => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?]/.test(password);

    const isValid = password.length >= minLength &&
      hasUpperCase &&
      hasLowerCase &&
      hasNumbers &&
      hasSpecialChar;

    if (!isValid) {
      console.log('Validación de contraseña:', {
        length: password.length >= minLength,
        upper: hasUpperCase,
        lower: hasLowerCase,
        numbers: hasNumbers,
        special: hasSpecialChar
      });
    }

    return isValid;
  };

  // Función para restablecer la contraseña
  const handleResetPassword = async () => {
    if (!selectedUser || !newPassword || !currentUser) return;

    if (!validatePassword(newPassword)) {
      onError?.(
        'La contraseña debe tener al menos 8 caracteres, incluir mayúsculas, minúsculas, números y caracteres especiales'
      );
      return;
    }

    setLoading(true);
    try {
      // Actualizar la contraseña usando Supabase Admin API
      const { error: updateError } = await supabaseAdmin.auth.admin.updateUserById(
        selectedUser.id,
        { password: newPassword }
      );

      if (updateError) throw updateError;

      // Actualizar el campo updated_by en profiles
      const { error: profileError } = await supabaseAdmin
        .from('profiles')
        .update({ 
          updated_by: currentUser.id,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedUser.id);

      if (profileError) throw profileError;

      // Registrar el cambio en password_change_logs
      const { error: logError } = await supabaseAdmin
        .from('password_change_logs')
        .insert({
          employee_id: selectedUser.id,
          performed_by: currentUser.id,
          details: `Contraseña restablecida por supervisor ${currentUser.name}`
        });

      if (logError) throw logError;

      onSuccess?.();
      setSelectedUser(null);
      setNewPassword('');
      setShowConfirmation(false);
    } catch (error) {
      console.error('Error al restablecer la contraseña:', error);
      onError?.(error instanceof Error ? error.message : 'Error al restablecer la contraseña');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-2xl font-semibold mb-6">Restablecer Contraseña de Usuario</h2>

      {/* Búsqueda de usuario */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Buscar Usuario
        </label>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            if (e.target.value.length >= 3) {
              searchUsers(e.target.value);
            }
          }}
          className="w-full px-4 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
          placeholder="Buscar por nombre o correo"
        />

        {/* Lista de usuarios encontrados */}
        {users.length > 0 && (
          <div className="mt-2 border rounded-md">
            {users.map((user) => (
              <div
                key={user.id}
                className="p-2 hover:bg-gray-50 cursor-pointer"
                onClick={() => {
                  setSelectedUser(user);
                  setUsers([]);
                  setSearchTerm('');
                }}
              >
                <div className="font-medium">{user.name}</div>
                <div className="text-sm text-gray-500">{user.email}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Usuario seleccionado y nueva contraseña */}
      {selectedUser && (
        <div className="space-y-4">
          <div className="p-4 bg-gray-50 rounded-md">
            <div className="font-medium">Usuario seleccionado:</div>
            <div>{selectedUser.name}</div>
            <div className="text-sm text-gray-500">{selectedUser.email}</div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nueva Contraseña
            </label>
            <div className="flex space-x-2">
              <input
                type="text"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="flex-1 px-4 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="Ingrese la nueva contraseña"
              />
              <button
                onClick={generateSecurePassword}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                type="button"
              >
                Generar
              </button>
            </div>
            {newPassword && (
              <div className="mt-2 p-2 bg-yellow-50 text-yellow-800 rounded-md text-sm">
                Asegúrese de copiar y proporcionar esta contraseña al usuario de forma segura.
              </div>
            )}
          </div>

          {/* Botones de acción */}
          <div className="flex justify-end space-x-2 mt-6">
            <button
              onClick={() => setSelectedUser(null)}
              className="px-4 py-2 text-gray-700 border rounded-md hover:bg-gray-50"
              type="button"
            >
              Cancelar
            </button>
            <button
              onClick={() => setShowConfirmation(true)}
              disabled={!newPassword || loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              type="button"
            >
              Restablecer Contraseña
            </button>
          </div>
        </div>
      )}

      {/* Modal de confirmación */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
            <h3 className="text-lg font-medium mb-4">Confirmar Restablecimiento de Contraseña</h3>
            <p className="text-gray-600 mb-4">
              ¿Está seguro que desea restablecer la contraseña de {selectedUser?.name}?
            </p>
            <p className="text-sm text-yellow-600 mb-6">
              Recuerde proporcionar la nueva contraseña al usuario de forma segura.
            </p>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowConfirmation(false)}
                className="px-4 py-2 text-gray-700 border rounded-md hover:bg-gray-50"
                type="button"
              >
                Cancelar
              </button>
              <button
                onClick={handleResetPassword}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                type="button"
              >
                {loading ? 'Procesando...' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
